import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../config/supabase';
import { User } from '@supabase/supabase-js';
import { ArrowLeft, UserCircle, LogOut, Globe, Loader, Check, ExternalLink, Users, CalendarDays } from 'lucide-react';
import { BIO_MAX, DISPLAY_NAME_MAX, USERNAME_RE, profilePath } from '../services/social';
import './ProfilePage.css';
import './Social.css';
import BlockedUsersCard from '../components/safety/BlockedUsersCard';

const ProfilePage: React.FC = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [username, setUsername] = useState('');
  const [worldCount, setWorldCount] = useState(0);
  // Public profile fields (/u/:username)
  const [handle, setHandle] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [bio, setBio] = useState('');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [saveErr, setSaveErr] = useState('');

  useEffect(() => {
    checkUser();
  }, []);

  const checkUser = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      navigate('/');
      return;
    }
    setUser(user);

    // Load profile
    const { data: profile } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', user.id)
      .single();
    if (profile) {
      setUsername(profile.username);
      setHandle(profile.username || '');
      setDisplayName(profile.display_name || '');
      setBio(profile.bio || '');
    }

    // Count worlds
    const { count } = await supabase
      .from('worlds')
      .select('id', { count: 'exact', head: true })
      .eq('owner_id', user.id);
    setWorldCount(count || 0);

    setLoading(false);
  };

  const handleOk = USERNAME_RE.test(handle.trim());

  const saveProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user || saving) return;
    const name = handle.trim();
    if (!USERNAME_RE.test(name)) { setSaveErr('Username: 3–20 letters, numbers, _ or .'); return; }
    setSaving(true); setSaveErr(''); setSaved(false);
    if (name !== username) {
      // ilike, escaped: "_" would otherwise match any character.
      const { data: taken } = await supabase.from('profiles').select('id')
        .ilike('username', name.replace(/[\\%_]/g, (c) => `\\${c}`)).neq('id', user.id).limit(1);
      if (taken && taken.length) { setSaving(false); setSaveErr('That username is taken.'); return; }
    }
    const { error } = await supabase.from('profiles').update({
      username: name,
      display_name: displayName.trim() || null,
      bio: bio.trim() || null,
    }).eq('id', user.id);
    if (error) {
      setSaving(false);
      setSaveErr(error.code === '23505' ? 'That username is taken.' : /display_name|bio/.test(error.message)
        ? 'Profile details aren’t switched on yet. Try again shortly.' : 'Couldn’t save your profile. Try again.');
      return;
    }
    if (name !== username) await supabase.auth.updateUser({ data: { username: name, username_confirmed: true } });
    setUsername(name);
    setSaving(false);
    setSaved(true);
    window.setTimeout(() => setSaved(false), 2200);
  };

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    navigate('/');
  };

  if (loading) {
    return (
      <div className="profile-loading">
        <Loader size={32} className="spinning" />
        <p>Loading profile...</p>
      </div>
    );
  }

  return (
    <div className="profile-page">
      {/* Header */}
      <header className="profile-header">
        <button className="profile-back-btn" onClick={() => navigate('/dashboard')}>
          <ArrowLeft size={18} /> Back to Dashboard
        </button>
        <h1>Profile</h1>
        <button className="profile-signout-btn" onClick={handleSignOut}>
          <LogOut size={16} /> Sign Out
        </button>
      </header>

      <div className="profile-content">
        {/* User info card */}
        <div className="profile-card">
          <div className="profile-card-header">
            <UserCircle size={48} className="profile-icon" />
            <div>
              <h2>{username || 'User'}</h2>
              <p className="profile-email">{user?.email}</p>
            </div>
          </div>
          <div className="profile-stats">
            <div className="profile-stat">
              <Globe size={16} />
              <span>{worldCount} world{worldCount !== 1 ? 's' : ''} created</span>
            </div>
          </div>
        </div>

        {/* Public profile: what people see at /u/:username */}
        <div className="profile-card">
          <div className="profile-card-header">
            <Users size={32} className="profile-icon" />
            <div>
              <h2>Public profile</h2>
              <p className="profile-email">Anyone can see this, along with your public worlds.</p>
            </div>
          </div>
          <form className="sx-form" onSubmit={saveProfile}>
            <label className="sx-field">
              <span>Username</span>
              <span className="sx-prefix">
                <em>{window.location.host}/u/</em>
                <input
                  className="sx-input"
                  value={handle}
                  onChange={(e) => setHandle(e.target.value.replace(/\s/g, ''))}
                  maxLength={20}
                  autoCapitalize="none"
                  autoCorrect="off"
                  spellCheck={false}
                  aria-invalid={!handleOk}
                />
              </span>
              {!handleOk
                ? <small className="err">3–20 letters, numbers, _ or .</small>
                : handle.trim() !== username
                  ? <small>Changing it changes your profile link; old links stop working.</small>
                  : null}
            </label>
            <label className="sx-field">
              <span>Display name</span>
              <input className="sx-input" value={displayName} onChange={(e) => setDisplayName(e.target.value)} maxLength={DISPLAY_NAME_MAX} placeholder={username} />
            </label>
            <label className="sx-field">
              <span>Bio</span>
              <textarea className="sx-input" value={bio} onChange={(e) => setBio(e.target.value)} maxLength={BIO_MAX} rows={3} placeholder="A line or two about you" />
              <small>{bio.length}/{BIO_MAX}</small>
            </label>
            {saveErr && <p className="sx-err" role="alert">{saveErr}</p>}
            <div className="sx-form-actions">
              <button className="sx-save" disabled={saving || !handleOk}>{saving ? 'Saving…' : 'Save profile'}</button>
              <a className="sx-link" href={profilePath(username)} target="_blank" rel="noopener noreferrer"><ExternalLink size={15} /> View public profile</a>
              <button type="button" className="sx-link" onClick={() => navigate('/connections')}><Users size={15} /> Connections</button>
              <button type="button" className="sx-link" onClick={() => navigate('/meetups')}><CalendarDays size={15} /> Meetups</button>
              {saved && <span className="sx-saved" role="status"><Check size={15} /> Saved</span>}
            </div>
          </form>
        </div>

        {/* Avatar management moved to its own page (/avatars) */}
        <div className="profile-card">
          <div className="profile-card-header">
            <UserCircle size={32} className="profile-icon" />
            <div>
              <h2>Avatars</h2>
              <p className="profile-email">Create, dress, and equip your avatars.</p>
            </div>
          </div>
          <button className="profile-back-btn" style={{ marginTop: 12 }} onClick={() => navigate('/avatars')}>
            Manage avatars
          </button>
        </div>

        {/* Safety: people you blocked or muted */}
        <BlockedUsersCard />
      </div>
    </div>
  );
};

export default ProfilePage;
